import { createSelector } from 'reselect'
import { getExperienceListKey, mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import { take, unique } from 'lib/array/arrayUtils'
import { EXP_CATEGORY_AIRPORT_TRANSFER_ID, EmptyExperienceList, EXPERIENCE_IN_FLOW_MAX_SHOWN, EXP_CATEGORY_AIRPORT_LOUNGE_ID } from 'constants/experience'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import config from 'constants/config'

export const getCartOfferGeoCoords = createSelector(
  (state: App.State) => state.cart.accommodation.offer?.property,
  (state: App.State) => state.cart.accommodation.package?.tour,
  (state: App.State) => state.cart.bedbank.offer?.property,
  (hotelProperty, tour, bedbankProperty) => {
    if (hotelProperty) {
      return {
        latitude: hotelProperty.latitude,
        longitude: hotelProperty.longitude,
      }
    } else if (tour) {
      return {
        latitude: tour.latitude,
        longitude: tour.longitude,
      }
    } else if (bedbankProperty) {
      return {
        latitude: bedbankProperty.latitude,
        longitude: bedbankProperty.longitude,
      }
    }

    return {}
  },
)

export const getCartExperienceDateRange = createSelector(
  (state: App.State) => state.cart.accommodation.startDate ?? state.cart.bedbank.checkIn,
  (state: App.State) => state.cart.accommodation.endDate ?? state.cart.bedbank.checkOut,
  (from, to) => {
    return {
      from: from ? moment(from).add(-7, 'days').format(ISO_DATE_FORMAT) : undefined,
      to: to ? moment(to).add(7, 'days').format(ISO_DATE_FORMAT) : undefined,
    }
  },
)

export const getCartExperienceOfferList = createSelector(
  (state: App.State) => getCartOfferGeoCoords(state),
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.config.experiments[config.AB_TEST_EXPERIENCE_ONLY_INFLOW_TRANSFERS] === '1' && !state.auth.account.isSpoofed,
  (geoCoords, currencyCode, lists, offer, inFlowTestEnabled) => {
    const isCurated = !!offer?.experiencesCurated
    const showOnlyCurated = !!offer?.showOnlyExperiencesCurated

    if (isCurated || showOnlyCurated) {
      const listKey = getExperienceListKey({
        currencyCode,
        offerId: offer.id,
        showUnlisted: true,
      })
      return lists[listKey] ?? EmptyExperienceList
    }

    const transferListKey = getExperienceListKey({
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      currencyCode,
      categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID],
    })

    // if not curated, we show post purchase at the front and then other experiences as fallbacks
    const postPurchaseListKey = getExperienceListKey({
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      currencyCode,
      postPurchaseOnly: true,
      categoryCodes: inFlowTestEnabled ? [EXP_CATEGORY_AIRPORT_TRANSFER_ID] : undefined,
    })

    const fallbackListKey = getExperienceListKey({
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      currencyCode,
      postPurchaseOnly: false,
      categoryCodes: inFlowTestEnabled ? [EXP_CATEGORY_AIRPORT_TRANSFER_ID] : undefined,
    })

    const transferList = lists[transferListKey] ?? EmptyExperienceList
    const postPurchaseList = lists[postPurchaseListKey] ?? EmptyExperienceList
    const fallbackList = lists[fallbackListKey] ?? EmptyExperienceList

    return {
      fetching: postPurchaseList.fetching || fallbackList.fetching || transferList.fetching,
      ids: take(
        unique([
          ...transferList.ids,
          ...postPurchaseList.ids,
          ...fallbackList.ids,
        ]),
        // take transfer ids + max of 10 of others (that aren't in the transfer list)!@@
        EXPERIENCE_IN_FLOW_MAX_SHOWN + transferList.ids.length,
      ),
    }
  },
)

export const getAllPurchasableExperiences = createSelector(
  (state: App.State) => getCartExperienceOfferList(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.cart.experiences.items,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.bedbank.offer,
  (list, experiences, cartExperienceItems, offer, bedbankOffer): Array<App.ExperienceBookingView> => {
    return list.ids.map(id => experiences[id]).filter(Boolean).map(exp =>
      mapExperienceToBookingView(exp, cartExperienceItems[exp.id], offer?.property ?? bedbankOffer?.property),
    )
  },
)
